import React, {useContext, useEffect, useRef, useState} from 'react';
import {Button, Col, Form, notification, Row, Slider} from 'antd';
import {useDebounce} from 'ahooks';
import {cloneDeep} from 'lodash-es';
import 'react-simple-keyboard/build/css/index.css';
/* eslint-disable @typescript-eslint/no-use-before-define */
import classNames from 'classnames';
import useCommon from '@utils/hooks/common';
import {CloseOutlined} from '@ant-design/icons';
import InsurancePoker from '@components/game-room/InsurancePanel/InsurancePoker';
import {GameLogicInterface} from 'poker-interfaces';
import styles from './styles';
import {Statistic} from 'antd';
import {PositionContext} from '@utils/context';

const FIELDS = {
  SELECTED_OUTS_CARDS: 'selectedOutsCards',
  PREMIUM: 'premium',
};

interface InsurancePanelInterface {
  insuranceInfo?: GameLogicInterface.Insurance;
  closePanel: () => void;
  confirmInsurance: (data: GameLogicInterface.BuyInsurance) => void;
  expiredAt: string;
}

const CardList = React.memo(
  (props: {
    insuranceInfo: InsurancePanelInterface['insuranceInfo'];
    onChange?: (value: Array<GameLogicInterface.CardObj>) => void;
    // eslint-disable-next-line react/no-unused-prop-types
    value?: Array<GameLogicInterface.CardObj>;
  }) => {
    const {insuranceInfo, onChange} = props;
    const selectedCardsRef = useRef<Array<GameLogicInterface.CardObj>>([]);

    const {classes, t} = useCommon({
      withTranslation: ['action_buttons'],
      withStyles: styles,
    });

    useEffect(() => {
      // Compare reference with prev UI re-render
      selectedCardsRef.current = [];
      onChange?.(insuranceInfo?.outs);
    }, [insuranceInfo]);

    if (!insuranceInfo?.outs || insuranceInfo?.outs?.length < 1) {
      return <div className={classes.insurancePanelFontWhite}>No Outs Cards available</div>;
    }

    const onSelectOutsCards = (pokerCard: GameLogicInterface.CardObj) => {
      const newSelectedCards: Array<GameLogicInterface.CardObj> = cloneDeep(
        selectedCardsRef.current
      );
      const pokerIndex = newSelectedCards.findIndex(
        ({rank, suit}) => rank === pokerCard.rank && suit === pokerCard.suit
      );
      if (pokerIndex !== -1) {
        const filteredArray = newSelectedCards.filter((_p, index) => index !== pokerIndex);
        selectedCardsRef.current = filteredArray;
        onChange?.(filteredArray);
      } else {
        newSelectedCards.push(pokerCard);
        selectedCardsRef.current = newSelectedCards;
        onChange?.(newSelectedCards);
      }
    };

    return (
      <div className={classes.insurancePanelCardsArea}>
        {insuranceInfo?.outs?.map(({suit, rank}, index) => (
          <InsurancePoker
            key={suit + rank + index}
            suit={suit}
            rank={rank}
            cardHeight={50}
            className={classes.myPokerCard}
            // TODO
            onClick={onSelectOutsCards}
          />
        ))}
      </div>
    );
  },
  (prevProps, nextProps) => {
    if (!nextProps?.insuranceInfo?.outs || !prevProps?.insuranceInfo?.outs) return false;
    const {outs: prevCards} = prevProps.insuranceInfo;
    const {outs: nextCards} = nextProps.insuranceInfo;

    if (prevCards === nextCards) return true;
    if (prevCards === null || nextCards === null) return false;
    if (prevCards.length !== nextCards.length) return false;

    const prevClone: Array<GameLogicInterface.CardObj> = cloneDeep(prevCards);
    const nextClone: Array<GameLogicInterface.CardObj> = cloneDeep(nextCards);
    prevClone.sort();
    nextClone.sort();

    for (let iii = 0; iii < prevClone.length; iii += 1) {
      if (
        prevClone[iii].suit !== nextClone[iii].suit ||
        prevClone[iii].rank !== nextClone[iii].rank
      ) {
        return false;
      }
    }
    return true;
  }
);
CardList.displayName = 'CardList';

const InsurancePanel = (props: InsurancePanelInterface): JSX.Element => {
  return null;
  const {classes, t} = useCommon({
    withTranslation: ['action_buttons'],
    withStyles: styles,
  });
  const {insuranceInfo, closePanel, confirmInsurance, expiredAt} = props;

  // console.log('InsurancePanel',InsurancePanel)

  const [premium, setPremium] = useState<number>(0.01);
  const [numOfSelectedCards, setNumOfSelectedCards] = useState<number>(0);
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
  const [cancelDisabled, setCancelDisabled] = useState<boolean>(false);
  const [form] = Form.useForm();

  const premiumDebounce = useDebounce(premium, {wait: 0});

  const {scaleUpValue} = useContext(PositionContext);

  useEffect(() => {
    const minPremium = insuranceInfo?.minPremium;
    form.resetFields();
    const formattedMinPremium = Math.round((minPremium + Number.EPSILON) * 1000) / 1000;
    setPremium(formattedMinPremium || 0.01);
    setNumOfSelectedCards(insuranceInfo?.outs?.length);
    if (insuranceInfo?.minPremium > 0) {
      setCancelDisabled(true);
    } else {
      setCancelDisabled(false);
    }
    if (insuranceInfo?.isForced) {
      setTimeout(() => {
        form.submit();
      }, 3000);
    }
  }, [insuranceInfo]);

  useEffect(() => {
    if (premiumDebounce === 0) {
      setPremium(0.01);
      setSubmitDisabled(true);
    } else {
      setSubmitDisabled(false);
    }
  }, [premiumDebounce]);

  return (
    <Form
      className={classes.antForm}
      form={form}
      initialValues={{
        [FIELDS.PREMIUM]: insuranceInfo?.minPremium || 0.01,
      }}
      onValuesChange={(changedFields: number | Array<GameLogicInterface.CardObj>) => {
        const [key, value] = Object.entries(changedFields)[0];
        if (key === FIELDS.PREMIUM) {
          setPremium(Math.round((value + Number.EPSILON) * 1000) / 1000);
        } else if (key === FIELDS.SELECTED_OUTS_CARDS) {
          setNumOfSelectedCards(value?.length ?? 0);
        }
      }}
      onFinish={values => {
        const selectedOuts = values[FIELDS.SELECTED_OUTS_CARDS];
        const premium = values[FIELDS.PREMIUM];
        if (!selectedOuts || selectedOuts?.length < 0) {
          notification.error({message: 'Not outs cards selected'});
          throw new Error('Not outs cards selected');
        } else if (premium < insuranceInfo?.minPremium || premium > insuranceInfo?.maxPremium) {
          throw new Error('Premium is out of range');
        }
        confirmInsurance({
          selectedOuts,
          premium,
        });
      }}
    >
      <Row className={classes.buttonsRow}>
        <Col span={14} className={classes.sliderColumn}>
          <div>
            <div className={classes.investmentLabel}>{t('investment')}</div>
            <Form.Item name={FIELDS.PREMIUM} noStyle>
              <Slider
                className={classes.insuranceSlider}
                tooltip={{open: false}}
                max={insuranceInfo?.maxPremium}
                min={insuranceInfo?.minPremium || 0.01}
                step={0.01}
              />
            </Form.Item>
          </div>
        </Col>
        <Col offset={2} span={8} className={classes.confirmColumn}>
          <Form.Item noStyle>
            {!insuranceInfo?.isForced ? (
              <Button
                disabled={submitDisabled}
                htmlType="submit"
                className={classes.insuranceConfirmButton}
                id="insuranceConfirmButton"
              >
                {t('confirm')} {' ( '}
                <Countdown
                  value={expiredAt}
                  format="mm:ss"
                  valueStyle={{
                    color: 'white',
                    lineHeight: '105%',
                    fontSize: '13px',
                    fontWeight: 'bold',
                  }}
                />
                {' )'}
              </Button>
            ) : (
              <Button
                disabled={submitDisabled}
                htmlType="submit"
                className={classes.insuraceForceButton}
              >
                {t('force_insurance')} {' ( '}
                <Countdown
                  value={Date.now() + 3000}
                  format="mm:ss"
                  valueStyle={{
                    color: 'white',
                    lineHeight: '105%',
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                />
                {' )'}
              </Button>
            )}
          </Form.Item>
        </Col>
        <div
          className={classNames(classes.insuranceInfoContainer)}
          style={{transform: `scale(${scaleUpValue})`}}
        >
          <Row className={classes.insurancePanelFontWhite}>
            <Col span={8}>{t('insurance')}</Col>
            <Col span={16}>
              <div
                className={classNames(classes.alignRight, cancelDisabled && classes.hidden)}
                onClick={() => {
                  if (!cancelDisabled) {
                    closePanel();
                  }
                }}
              >
                <CloseOutlined id="insuranceCancel" className={classes.closeIcon} />
              </div>
            </Col>
          </Row>
          <Row>
            <Col span={8} className={classes.insurancePanelDetails}>
              {t('choosing_cards')}
            </Col>
            <Col span={16} className={classes.insurancePanelDetails}>
              <div className={classes.alignRight}>
                {t('outs_number')}
                <span
                  className={classNames(classes.insurancePanelFontWhite, classes.numOfOutCards)}
                >
                  {insuranceInfo?.outs?.length}
                </span>
              </div>
            </Col>
          </Row>
          <Form.Item name={FIELDS.SELECTED_OUTS_CARDS} noStyle>
            <CardList insuranceInfo={insuranceInfo} />
          </Form.Item>
          <Row className={classes.insuranceSelectionContainer}>
            <Col span={4}>
              <div className={classes.insurancePanelDetails}>{t('odds')}</div>
              <div className={classes.insurancePanelFontWhite}>{insuranceInfo?.odds || 0}</div>
            </Col>
            <Col span={3} className={classes.insurancePanelFontWhite}>
              <div className={classes.insurancePanelTimes}>x</div>
            </Col>
            <Col span={4}>
              <div className={classes.insurancePanelDetails}>{t('premium')}</div>
              <div className={classes.insurancePanelFontMagenta}>
                ${Number.isNaN(premiumDebounce) ? null : premiumDebounce}
              </div>
            </Col>
            <Col span={3} className={classes.insurancePanelFontWhite}>
              <div className={classes.insurancePanelEqual}>=</div>
            </Col>
            <Col span={5}>
              <div className={classes.insurancePanelDetails}>{t('payout')}</div>
              <div className={classes.insurancePanelFontBlue}>
                ${Math.round((premium * (insuranceInfo?.odds || 0) + Number.EPSILON) * 100) / 100}
              </div>
            </Col>
          </Row>
        </div>
      </Row>
    </Form>
  );
};



export default React.memo(InsurancePanel);
